import { useState, useEffect, useMemo } from 'react'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { subscribeToDeals, subscribeToAllDeals, createDeal, updateDeal, updateDealStage, deleteDeal } from '../firebase/deals'
import { getOrCreatePipeline } from '../firebase/deals'
import DealForm from './DealForm'
import './Deals.css'

const DealCard = ({ deal, onDelete, onEdit, showOwner }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: deal.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="deal-card"
    >
      <div className="deal-header">
        <h4 className="deal-name">{deal.name}</h4>
        <div
          className="deal-card-actions"
          onClick={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
        >
          <button
            type="button"
            className="deal-edit-btn"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onEdit(deal)
            }}
            title="Edit deal"
          >
            Edit
          </button>
          <button
            type="button"
            className="deal-delete-btn"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDelete(deal.id, e)
            }}
            title="Delete deal"
          >
            ×
          </button>
        </div>
      </div>
      {deal.value && (
        <div className="deal-value">{formatCurrency(deal.value)}</div>
      )}
      {deal.contactName && (
        <div className="deal-contact">👤 {deal.contactName}</div>
      )}
      {deal.contactEmail && (
        <div className="deal-contact-email">
          <a href={`mailto:${deal.contactEmail}`} onPointerDown={(e) => e.stopPropagation()}>{deal.contactEmail}</a>
        </div>
      )}
      {deal.company && (
        <div className="deal-company">🏢 {deal.company}</div>
      )}
      {showOwner && (
        <div className="deal-owner" title={deal.owner || ''}>
          Rep:{' '}
          <span className="deal-owner-email">{deal.ownerEmail || (deal.owner ? `${deal.owner.slice(0, 10)}…` : '—')}</span>
        </div>
      )}
    </div>
  )
}

const DealColumn = ({ stage, deals, onDelete, onEdit, showOwner }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id
  })

  return (
    <div 
      ref={setNodeRef}
      className={`pipeline-column ${isOver ? 'drag-over' : ''}`}
    >
      <div className="column-header">
        <h3>{stage.name}</h3>
        <span className="deal-count">{deals.length}</span>
      </div>
      <SortableContext
        items={deals.map(d => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="column-content">
          {deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} onDelete={onDelete} onEdit={onEdit} showOwner={showOwner} />
          ))}
          {deals.length === 0 && (
            <div className="empty-column">Drop deals here</div>
          )}
        </div>
      </SortableContext>
    </div>
  )
}

const Deals = ({ user, isAdmin = false }) => {
  const [deals, setDeals] = useState([])
  const [pipeline, setPipeline] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dealFormOpen, setDealFormOpen] = useState(false)
  const [dealToEdit, setDealToEdit] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, dealId: null, dealName: '' })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  useEffect(() => {
    if (!user) return

    // Load pipeline
    const loadPipeline = async () => {
      try {
        const pipelineData = await getOrCreatePipeline(user.uid)
        setPipeline(pipelineData)
      } catch (error) {
        console.error('Error loading pipeline:', error)
      }
    }

    loadPipeline()

    const unsubscribe = isAdmin
      ? subscribeToAllDeals((dealsData) => {
          setDeals(dealsData)
          setLoading(false)
        })
      : subscribeToDeals(user.uid, (dealsData) => {
          setDeals(dealsData)
          setLoading(false)
        })

    return () => unsubscribe()
  }, [user, isAdmin])

  const displayStages = useMemo(() => {
    if (!pipeline?.stages?.length) return []
    const knownIds = new Set(pipeline.stages.map((s) => s.id))
    const extraIds = [...new Set(deals.map((d) => d.stage).filter(Boolean))].filter((id) => !knownIds.has(id))
    const extras = extraIds.map((id) => ({
      id,
      name: id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      order: 999
    }))
    return [...pipeline.stages, ...extras]
  }, [pipeline, deals])

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (!over) {
      return
    }

    // Find the deal
    const deal = deals.find(d => d.id === active.id)
    if (!deal) return

    // Check if dropped on a stage (column) or another deal
    const newStage = displayStages.find(s => s.id === over.id)
      ? over.id.toString()
      : deals.find(d => d.id === over.id)?.stage

    // If no valid stage found or same stage, do nothing
    if (!newStage || newStage === deal.stage) {
      return
    }

    const oldStage = deal.stage

    // Optimistically update UI
    setDeals(prevDeals =>
      prevDeals.map(d =>
        d.id === active.id ? { ...d, stage: newStage } : d
      )
    )

    // Update in Firestore
    try {
      await updateDealStage(active.id, newStage)
    } catch (error) {
      console.error('Error updating deal stage:', error)
      // Revert on error
      setDeals(prevDeals =>
        prevDeals.map(d =>
          d.id === active.id ? { ...d, stage: oldStage } : d
        )
      )
      alert('Failed to update deal stage. Please try again.')
    }
  }

  const closeDealForm = () => {
    setDealFormOpen(false)
    setDealToEdit(null)
  }

  const sanitizeDealPayload = (formData) => ({
    name: String(formData.name || '').trim(),
    value: typeof formData.value === 'number' ? formData.value : parseFloat(String(formData.value)) || 0,
    contactName: formData.contactName?.trim() || null,
    contactEmail: formData.contactEmail?.trim() || null,
    company: formData.company?.trim() || null,
    stage: formData.stage
  })

  const handleCreateDeal = async (dealData) => {
    try {
      await createDeal({ ...sanitizeDealPayload(dealData) }, user.uid)
      closeDealForm()
    } catch (error) {
      console.error('Error creating deal:', error)
      alert('Failed to create deal. Please try again.')
    }
  }

  const handleUpdateDeal = async (dealId, formData) => {
    try {
      await updateDeal(dealId, sanitizeDealPayload(formData))
      closeDealForm()
    } catch (error) {
      console.error('Error updating deal:', error)
      alert('Failed to update deal. Please try again.')
    }
  }

  const openNewDealForm = () => {
    setDealToEdit(null)
    setDealFormOpen(true)
  }

  const openEditDealForm = (deal) => {
    setDealToEdit(deal)
    setDealFormOpen(true)
  }

  const handleDeleteDeal = async (dealId, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    // Find deal name for confirmation
    const deal = deals.find(d => d.id === dealId)
    const dealName = deal?.name || 'this deal'
    
    // Show custom confirmation modal
    setDeleteConfirm({ show: true, dealId, dealName })
  }

  const confirmDelete = async () => {
    const { dealId } = deleteConfirm
    
    if (!user || !user.uid) {
      console.error('User not authenticated')
      alert('User not authenticated. Please refresh the page.')
      setDeleteConfirm({ show: false, dealId: null, dealName: '' })
      return
    }

    try {
      await deleteDeal(dealId)
      setDeleteConfirm({ show: false, dealId: null, dealName: '' })
      alert('✅ Deal deleted successfully')
    } catch (error) {
      console.error('Error deleting deal:', error)
      alert(`❌ Failed to delete deal: ${error.message || 'Unknown error'}. Please try again.`)
      setDeleteConfirm({ show: false, dealId: null, dealName: '' })
    }
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, dealId: null, dealName: '' })
  }

  const getDealsByStage = (stageId) => {
    return deals.filter(deal => deal.stage === stageId)
  }

  if (loading || !pipeline) {
    return <div className="loading">Loading deals...</div>
  }

  return (
    <div className="deals-page">
      <div className="page-header">
        <button className="btn-primary" type="button" onClick={openNewDealForm}>
          + New Deal
        </button>
        <div>
          <h2>Deal Pipeline</h2>
          <p className="subtitle">
            {deals.length} total deals{isAdmin ? ' (all users)' : ''}
          </p>
        </div>
      </div>

      {dealFormOpen && (
        <DealForm
          key={dealToEdit?.id ?? 'new'}
          pipeline={pipeline}
          stageOptions={displayStages}
          initialData={
            dealToEdit
              ? {
                  name: dealToEdit.name ?? '',
                  value: dealToEdit.value ?? '',
                  contactName: dealToEdit.contactName ?? '',
                  contactEmail: dealToEdit.contactEmail ?? '',
                  company: dealToEdit.company ?? '',
                  stage: dealToEdit.stage ?? pipeline.stages[0]?.id
                }
              : null
          }
          onSubmit={
            dealToEdit ? (fd) => handleUpdateDeal(dealToEdit.id, fd) : handleCreateDeal
          }
          contactsUserId={
            dealToEdit?.owner && isAdmin ? dealToEdit.owner : user.uid
          }
          onCancel={closeDealForm}
          userId={user.uid}
        />
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="pipeline-container">
          {displayStages.map((stage) => {
            const stageDeals = getDealsByStage(stage.id)
            return (
              <DealColumn
                key={stage.id}
                stage={stage}
                deals={stageDeals}
                onDelete={handleDeleteDeal}
                onEdit={openEditDealForm}
                showOwner={isAdmin}
              />
            )
          })}
        </div>
      </DndContext>

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete Deal</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteConfirm.dealName}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete Deal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Deals

